import { notification } from 'antd';
import { batch, useSelector } from 'react-redux';
import { ThunkDispatch } from 'redux-thunk';
import { GeniePadElementsSettingItem } from '../../api/pushGenie';
import { PadDForm } from '../../api/pushVisit';
import { PAD_CONFIG_FOLLOW_UP_ADVICES_ID } from '../../constants';
import { Actions } from '../../store/actions';
import { GlobalStateV1 } from '../../store/reducers';
import { CopyType, Tool } from '../../store/types/prescription';
import { track } from '../../tracking';
import { Visits } from '../prescriptionPad/rightPanel/workspace/PastVisits';
import { PadElements } from '../prescriptionPad/SummaryView';
import { configs } from '../../pages/prescriptionPad/magicBox/VisitsConfigs';
import { DEFAULT_CONFIG } from '../../pages/prescriptionPad/magicBox/VisitsDefaultConfig';
import Wrapper from '../../pages/prescriptionPad/magicBox/Wrapper';
import { getSectionNameConfig } from '../../store/selectors/session';
import { getSectionNameFromKey } from '../../pages/utils';

export const onCopy = (
    key: 'PX' | keyof Tool,
    visit: Tool & { date: string },
    allSections: string[],
    dispatch: ThunkDispatch<GlobalStateV1, null, Actions>,
    type?: CopyType,
) => {
    const config = configs[key as keyof PadElements] || DEFAULT_CONFIG;

    let actions: Actions[] | undefined | Actions = [];
    if (key === 'PX') {
        allSections.forEach((section) => {
            const config = configs[section as keyof PadElements] || DEFAULT_CONFIG;
            const action = config.createCopyAction(visit, section as keyof Tool);
            if (Array.isArray(actions)) {
                if (Array.isArray(action)) {
                    actions = actions.concat(...(action as Actions[]));
                } else {
                    actions.push(action as Actions);
                }
            }
        });
    } else {
        actions = config.createCopyAction(visit, key, type);
    }

    if (Array.isArray(actions)) {
        actions = (actions as Actions[]).filter(Boolean);
        batch(() => {
            (actions as Actions[]).forEach((action) => dispatch(action));
        });
    } else if (actions) {
        dispatch(actions);
    }

    track('PAST_VISIT_BOX_COPY', { key });

    let message = 'Copied to Pad';
    if (key === 'careCanvas') {
        message = 'Copied to Canvas';
    } else if (key === 'PX') {
        if (visit.careCanvas && visit.careCanvas.length > 0) {
            message = `Copied to Canvas and Pad`;
        }
    }

    notification.success({
        message: message,
        duration: 5,
        placement: 'bottomRight',
    });
};

// TODO: reuse the same in summary view
export const getAllPadSections = (
    padElementsConfig: GeniePadElementsSettingItem[],
    dyForms?: PadDForm[] | undefined,
) => {
    const remainingPxElements = Object.keys(configs).filter((elem) => {
        if (!padElementsConfig?.find((e) => e.id === elem)) {
            return true;
        }
        return false;
    });

    const remainingDyForms = Array.from(
        new Set(
            dyForms
                ?.filter((t) => !padElementsConfig.find((ec) => ec.id === t.id))
                .map((item) => item.id) || [],
        ),
    );

    const allSections = [
        ...padElementsConfig
            ?.filter((t) => t.isShown)
            .filter(
                (t) =>
                    t.id !== 'vitals' &&
                    t.id !== 'patientHistory' &&
                    t.id !== 'labRefer' &&
                    t.id !== PAD_CONFIG_FOLLOW_UP_ADVICES_ID,
            )
            ?.map((item) => item.id),
        ...remainingPxElements,
        ...remainingDyForms,
    ];

    return allSections;
};

export const getAllPastVisitComponent = (
    allSections: string[],
    visit: Visits,
    dispatch: ThunkDispatch<GlobalStateV1, null, Actions>,
): JSX.Element[] => {
    const padElementsConfig = useSelector(getSectionNameConfig);

    const components = allSections.map((id) => {
        const config = configs[id as keyof PadElements] || DEFAULT_CONFIG;
        return (
            <Wrapper
                iconText={config.icon.text}
                iconCssClass={config.icon.cssClass}
                iconTooltipContent={
                    getSectionNameFromKey(id, padElementsConfig) ||
                    config.icon.tooltipContent(visit, id as keyof Tool)
                }
                // eslint-disable-next-line @typescript-eslint/ban-ts-comment
                // @ts-ignore
                content={config.content(visit, id, undefined, dispatch)}
                onCopy={(type) => onCopy(id as keyof Tool, visit, allSections, dispatch, type)}
                id={id}
            />
        );
    });

    return components;
};

export const checkIsEmpty = (allSections: string[], visit: Visits): boolean => {
    const isEmpty =
        allSections
            .map((id) => {
                const config = configs[id as keyof PadElements] || DEFAULT_CONFIG;
                return config.content(visit, id as keyof Tool);
            })
            .filter(Boolean).length === 0;

    return isEmpty;
};
